import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Search, X, Eye, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { CountryCombobox } from '@/components/CountryCombobox';
import { CountryFilter } from '@/components/CountryFilter';
import { DEFAULT_COUNTRY } from '@/lib/countries';

export default function ClubsPage({ embedded = false, country: externalCountry, autoOpenCreate = false, onAutoOpenConsumed }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrgType, setFilterOrgType] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [internalCountry, setInternalCountry] = useState('');
  // When embedded inside People & Places the parent owns the country
  // filter — fall back to internal state otherwise.
  const countryFilter = embedded ? (externalCountry || '') : internalCountry;
  const setCountryFilter = setInternalCountry;
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingClub, setViewingClub] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    abbreviation: '',
    organisation_type: 'Club',
    competition_region: '',
    offers_artistic: true,
    offers_figures: false,
    contact_email: '',
    country: ''
  });

  const isSuper = currentUser?.role === 'super';

  const usersById = useMemo(
    () => Object.fromEntries((users || []).map(u => [u.user_id, u])),
    [users]
  );

  const orgTypes = ['Club', 'Regional Committee', 'Area/State Committee', 'National Body', 'International Body', 'WorldSkate'];

  const fetchClubs = async (country) => {
    try {
      const params = country ? { country } : {};
      const response = await axios.get(`${API}/clubs`, { withCredentials: true, params });
      setClubs(response.data);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  // Refetch whenever the country filter changes (initial load included
  // — ``CountryFilter`` reports its resolved default on first mount).
  useEffect(() => {
    if (!countryFilter) return;
    fetchClubs(countryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFilter]);

  // Load users only for super — they need names for owner display + reassign UI.
  useEffect(() => {
    if (!isSuper) return;
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => setUsers(r.data || [])).catch(() => {});
  }, [isSuper]);

  const canEditClub = (club) => {
    if (!currentUser) return false;
    if (isSuper) return true;
    return club.owner_user_id === currentUser.user_id;
  };
  const canDeleteClub = canEditClub;
  const canCreate = !!currentUser;
  // Standard users who don't own the club can request a reassignment.
  const canRequestReassign = (club) => (
    !!currentUser && !isSuper && club.owner_user_id !== currentUser.user_id
  );

  const [reassignRequestFor, setReassignRequestFor] = useState(null);
  const [reassignReason, setReassignReason] = useState('');
  const [reassignRequesting, setReassignRequesting] = useState(false);
  // Payload surfaced when delete returns 409 — list of competitions
  // still using the club as host.
  const [deleteBlockers, setDeleteBlockers] = useState(null);
  
  // Filter and sort clubs: alphabetical by name, with search and org type filter
  const filteredClubs = useMemo(() => {
    let result = [...clubs];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(club => 
        club.name.toLowerCase().includes(term) ||
        club.short_name?.toLowerCase().includes(term) ||
        club.abbreviation?.toLowerCase().includes(term) ||
        club.competition_region?.toLowerCase().includes(term)
      );
    }
    
    // Filter by organisation type
    if (filterOrgType) {
      result = result.filter(club => club.organisation_type === filterOrgType);
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => a.name.localeCompare(b.name));
    
    return result;
  }, [clubs, searchTerm, filterOrgType]);
  
  // Get unique org types from data for filter dropdown
  const availableOrgTypes = useMemo(() => {
    const types = [...new Set(clubs.map(c => c.organisation_type))];
    return types.sort();
  }, [clubs]);
  
  const resetForm = () => {
    setFormData({
      name: '',
      short_name: '',
      abbreviation: '',
      organisation_type: 'Club',
      competition_region: '',
      offers_artistic: true,
      offers_figures: false,
      contact_email: '',
      country: '',
    });
    setEditingClub(null);
  };

  /** Called before opening the create-club dialog so the country
   * defaults to the calling user's country (falling back to New
   * Zealand). Keeps a single source of truth for the resolution rule. */
  const openCreateDialog = async () => {
    let country = DEFAULT_COUNTRY;
    try {
      const r = await axios.get(`${API}/countries/my-default`, { withCredentials: true });
      if (r.data?.country) country = r.data.country;
    } catch { /* keep the fallback */ }
    setFormData({
      name: '',
      short_name: '',
      abbreviation: '',
      organisation_type: 'Club',
      competition_region: '',
      offers_artistic: true,
      offers_figures: false,
      contact_email: '',
      country,
    });
    setEditingClub(null);
    setOpen(true);
  };

  // Support ?action=create deep-link from the Dashboard Quick Action.
  useEffect(() => {
    if (!autoOpenCreate) return;
    openCreateDialog();
    if (onAutoOpenConsumed) onAutoOpenConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCreate]);

  const handleEdit = (club) => {
    setFormData({
      name: club.name,
      short_name: club.short_name,
      abbreviation: club.abbreviation,
      organisation_type: club.organisation_type,
      competition_region: club.competition_region,
      offers_artistic: club.offers_artistic,
      offers_figures: club.offers_figures,
      contact_email: club.contact_email,
      country: club.country || DEFAULT_COUNTRY,
    });
    setEditingClub(club);
    setOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClub) {
        const r = await axios.patch(`${API}/clubs/${editingClub.club_id}`, formData, { withCredentials: true });
        if (r.data?.status === 'pending') {
          toast.success(r.data.message || 'Change submitted for approval.');
        } else {
          toast.success('Club updated!');
        }
      } else {
        await axios.post(`${API}/clubs`, formData, { withCredentials: true });
        toast.success('Club created!');
      }
      setOpen(false);
      resetForm();
      fetchClubs(countryFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingClub ? 'Failed to update club' : 'Failed to create club'));
    }
  };

  const submitReassign = async () => {
    if (!reassignFor || !reassignTargetId) return;
    try {
      await axios.post(
        `${API}/clubs/${reassignFor.club_id}/reassign-owner`,
        { user_id: reassignTargetId },
        { withCredentials: true }
      );
      toast.success('Owner reassigned.');
      setReassignFor(null);
      setReassignTargetId('');
      fetchClubs(countryFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reassign failed');
    }
  };
  
  const handleDelete = async (id) => {
    if (!confirm('Archive this organisation? Historical records will be preserved.')) return;
    setDeleteBlockers(null);
    try {
      await axios.delete(`${API}/clubs/${id}`, { withCredentials: true });
      toast.success('Organisation archived');
      fetchClubs(countryFilter);
    } catch (error) {
      // 409 → the club is still hosting active competitions. Surface
      // the blocker list in a dialog so the owner knows what to do.
      if (error.response?.status === 409) {
        const detail = error.response?.data?.detail || {};
        setDeleteBlockers({
          club_id: id,
          message: detail.message || 'This organisation is still hosting active competitions.',
          competitions: detail.competitions || [],
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to archive organisation');
      }
    }
  };

  const submitReassignRequest = async () => {
    if (!reassignRequestFor) return;
    setReassignRequesting(true);
    try {
      const r = await axios.post(
        `${API}/clubs/${reassignRequestFor.club_id}/request-reassignment`,
        { reason: reassignReason.trim() },
        { withCredentials: true },
      );
      toast.success(r.data?.message || 'Reassignment requested');
      setReassignRequestFor(null);
      setReassignReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request reassignment');
    } finally {
      setReassignRequesting(false);
    }
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setFilterOrgType('');
  };

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6 fade-in'}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          {embedded ? (
            <div>
              <h2 className="text-xl font-semibold" style={{ color: '#2D4A3E' }}>Skating Organisations</h2>
              <p className="text-sm text-muted-foreground">Clubs, Committees, National Skating Organisations and more</p>
            </div>
          ) : (
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              Clubs & Organisations
            </h1>
          )}
          {!embedded && <CountryFilter value={countryFilter} onChange={setCountryFilter} testId="clubs-country-filter" />}
        </div>
        <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
          {canCreate && (
            <Button className="btn-primary" data-testid="create-club-button" onClick={openCreateDialog}>
              <Plus className="mr-2 h-5 w-5" />
              New Organisation
            </Button>
          )}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClub ? 'Edit Club' : 'Create Club'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="club-name-input" />
                </div>
                <div>
                  <Label>Short Name *</Label>
                  <Input value={formData.short_name} onChange={(e) => setFormData({...formData, short_name: e.target.value})} required data-testid="club-shortname-input" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Abbreviation * (Max 5 chars)</Label>
                  <Input value={formData.abbreviation} onChange={(e) => setFormData({...formData, abbreviation: e.target.value.slice(0, 5)})} required maxLength={5} data-testid="club-abbreviation-input" />
                </div>
                <div>
                  <Label>Organisation Type *</Label>
                  <select className="w-full border rounded-lg px-3 py-2" value={formData.organisation_type} onChange={(e) => setFormData({...formData, organisation_type: e.target.value})} data-testid="club-orgtype-select">
                    {orgTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </div>
              
              <div>
                <Label>Competition Region *</Label>
                <Input value={formData.competition_region} onChange={(e) => setFormData({...formData, competition_region: e.target.value})} required data-testid="club-region-input" />
              </div>
              
              <div>
                <Label>Disciplines Offered * (At least one required)</Label>
                <div className="flex gap-6 mt-2">
                  <div className="flex items-center gap-2">
                    <Checkbox checked={formData.offers_artistic} onCheckedChange={(val) => setFormData({...formData, offers_artistic: val})} data-testid="club-artistic-checkbox" />
                    <span>Artistic</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={formData.offers_figures} onCheckedChange={(val) => setFormData({...formData, offers_figures: val})} data-testid="club-figures-checkbox" />
                    <span>Figures</span>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Contact Email *</Label>
                <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} required data-testid="club-email-input" />
              </div>

              <div>
                <Label>Country *</Label>
                <div className="mt-1">
                  <CountryCombobox
                    value={formData.country}
                    onChange={(v) => setFormData({ ...formData, country: v })}
                    testId="club-country"
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full btn-primary" data-testid="club-submit-button">
                {editingClub ? 'Update Club' : 'Create Club'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Search and Filter Controls */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm text-muted-foreground mb-1 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, abbreviation, or region..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="clubs-search-input"
              />
            </div>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-sm text-muted-foreground mb-1 block">Organisation Type</Label>
            <select 
              className="w-full border rounded-lg px-3 py-2 h-10"
              value={filterOrgType}
              onChange={(e) => setFilterOrgType(e.target.value)}
              data-testid="clubs-filter-orgtype"
            >
              <option value="">All Types</option>
              {availableOrgTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          {(searchTerm || filterOrgType) && (
            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="h-10"
              data-testid="clubs-clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
        {(searchTerm || filterOrgType) && (
          <div className="mt-3 text-sm text-muted-foreground">
            Showing {filteredClubs.length} of {clubs.length} organisations
          </div>
        )}
      </Card>
      
      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 px-2 font-semibold">Name</th>
                <th className="text-left py-3 px-2 font-semibold">Abbreviation</th>
                <th className="text-left py-3 px-2 font-semibold">Org Type</th>
                <th className="text-center py-3 px-2 font-semibold">Artistic</th>
                <th className="text-center py-3 px-2 font-semibold">Figures</th>
                <th className="text-left py-3 px-2 font-semibold">Contact Email</th>
                {isSuper && <th className="text-left py-3 px-2 font-semibold">Owner</th>}
                <th className="text-center py-3 px-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClubs.map(club => {
                const canEdit = canEditClub(club);
                const ownerName = usersById[club.owner_user_id]?.name || club.owner_user_id || '—';
                return (
                <tr key={club.club_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`club-row-${club.club_id}`}>
                  <td className="py-3 px-2">
                    <div>
                      <div className="font-medium">{club.name}</div>
                      <div className="text-sm text-muted-foreground">{club.short_name}</div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm">{club.abbreviation}</td>
                  <td className="py-3 px-2 text-sm">{club.organisation_type}</td>
                  <td className="py-3 px-2 text-center text-sm">{club.offers_artistic ? 'Yes' : 'No'}</td>
                  <td className="py-3 px-2 text-center text-sm">{club.offers_figures ? 'Yes' : 'No'}</td>
                  <td className="py-3 px-2 text-sm">{club.contact_email}</td>
                  {isSuper && (
                    <td className="py-3 px-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-xs">{ownerName}</span>
                        <button
                          onClick={() => { setReassignFor(club); setReassignTargetId(''); }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          data-testid={`reassign-owner-${club.club_id}`}
                          title="Reassign owner"
                        >
                          Reassign
                        </button>
                      </div>
                    </td>
                  )}
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => { setViewingClub(club); setViewOpen(true); }} className="action-icon" title="View" data-testid={`view-club-${club.club_id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      {canEdit && (
                        <button onClick={() => handleEdit(club)} className="action-icon" title="Edit" data-testid={`edit-club-${club.club_id}`}>
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                      {canRequestReassign(club) && (
                        <button
                          onClick={() => { setReassignRequestFor(club); setReassignReason(''); }}
                          className="action-icon text-indigo-700 hover:text-indigo-900"
                          title="Request ownership reassignment"
                          data-testid={`request-reassign-${club.club_id}`}
                        >
                          <UserCog className="h-4 w-4" />
                        </button>
                      )}
                      {canDeleteClub(club) && (
                        <button
                          onClick={() => handleDelete(club.club_id)}
                          className="action-icon text-red-600 hover:text-red-800"
                          title="Archive"
                          data-testid={`delete-club-${club.club_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {filteredClubs.length === 0 && clubs.length > 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No organisations match your search criteria.
            </div>
          )}
          {clubs.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No organisations yet. Create your first one!
            </div>
          )}
        </div>
      </Card>

      {/* Reassign owner dialog (super only) */}
      <Dialog open={!!reassignFor} onOpenChange={(v) => { if (!v) setReassignFor(null); }}>
        <DialogContent data-testid="reassign-owner-dialog">
          <DialogHeader>
            <DialogTitle>Reassign owner of {reassignFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose a user who is currently a member of this club (or another super user for temporary custody).
            </p>
            <select
              className="w-full border rounded-lg px-3 py-2 bg-white"
              value={reassignTargetId}
              onChange={(e) => setReassignTargetId(e.target.value)}
              data-testid="reassign-owner-select"
            >
              <option value="">-- Select user --</option>
              {users
                .filter(u => u.role === 'super' || u.club_id === reassignFor?.club_id)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(u => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name || u.email}
                    {u.role === 'super' ? ' (super)' : ''}
                  </option>
                ))}
            </select>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={() => setReassignFor(null)} data-testid="reassign-cancel-btn">
                Cancel
              </Button>
              <Button
                className="btn-primary"
                onClick={submitReassign}
                disabled={!reassignTargetId}
                data-testid="reassign-confirm-btn"
              >
                Reassign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent data-testid="view-club-dialog">
          <DialogHeader>
            <DialogTitle>Organisation Details</DialogTitle>
          </DialogHeader>
          {viewingClub && (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">Name:</span> <strong>{viewingClub.name}</strong></div>
              <div><span className="text-muted-foreground">Short name:</span> {viewingClub.short_name}</div>
              <div><span className="text-muted-foreground">Abbreviation:</span> {viewingClub.abbreviation}</div>
              <div><span className="text-muted-foreground">Type:</span> {viewingClub.organisation_type}</div>
              <div><span className="text-muted-foreground">Region:</span> {viewingClub.competition_region}</div>
              <div><span className="text-muted-foreground">Country:</span> {viewingClub.country || '—'}</div>
              <div><span className="text-muted-foreground">Contact email:</span> {viewingClub.contact_email}</div>
              <div><span className="text-muted-foreground">Offers artistic:</span> {viewingClub.offers_artistic ? 'Yes' : 'No'}</div>
              <div><span className="text-muted-foreground">Offers figures:</span> {viewingClub.offers_figures ? 'Yes' : 'No'}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reassignment-request dialog (standard-user, non-owner) */}
      <Dialog open={!!reassignRequestFor} onOpenChange={(v) => { if (!v) { setReassignRequestFor(null); setReassignReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="reassign-request-dialog">
          <DialogHeader>
            <DialogTitle>Request ownership reassignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Ask a super user to transfer ownership of
              <strong> {reassignRequestFor?.name}</strong> to you. Super users review requests before granting access.
            </p>
            <div>
              <Label className="text-sm">Reason (optional)</Label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 mt-1 min-h-[80px] text-sm"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="Why you should take over as owner — e.g. role change, previous owner no longer active, etc."
                data-testid="reassign-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setReassignRequestFor(null); setReassignReason(''); }} disabled={reassignRequesting}>Cancel</Button>
              <Button
                className="btn-primary"
                onClick={submitReassignRequest}
                disabled={reassignRequesting}
                data-testid="reassign-request-submit"
              >
                {reassignRequesting ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete-blocked dialog — surfaces the competitions still
          using the club as host so the owner can reassign first. */}
      <Dialog open={!!deleteBlockers} onOpenChange={(v) => { if (!v) setDeleteBlockers(null); }}>
        <DialogContent className="max-w-lg" data-testid="delete-blockers-dialog">
          <DialogHeader>
            <DialogTitle>Can't archive — competitions still using this organisation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{deleteBlockers?.message}</p>
            {deleteBlockers?.competitions?.length > 0 && (
              <div className="border rounded bg-stone-50 p-3 text-sm">
                <ul className="space-y-1 list-disc pl-5">
                  {deleteBlockers.competitions.map((c) => (
                    <li key={c.competition_id}>
                      <strong>{c.name}</strong>
                      {c.status ? <span className="text-muted-foreground"> · {c.status}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Reassign the host organisation on each competition (via Edit Competition → Host Organisation), then try again.
            </p>
            <div className="flex justify-end pt-2">
              <Button className="btn-primary" onClick={() => setDeleteBlockers(null)}>Okay</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
